import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MediaTrack } from '@/lib/media';
import { incrementTrackPlay, likeTrack, formatCount, isYoutubeUrl } from '@/lib/media';
import YoutubeEmbed from './YoutubeEmbed';

interface MusicPlayerProps {
  tracks: MediaTrack[];
}

function formatTime(seconds: number): string {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function MusicPlayer({ tracks }: MusicPlayerProps) {
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const tracksRef = useRef<MediaTrack[]>(tracks);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [youtubeTrack, setYoutubeTrack] = useState<MediaTrack | null>(null);

  const currentTrack = currentIndex !== null ? tracks[currentIndex] : null;

  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('timeupdate', () => {
        setProgress(audioRef.current?.currentTime || 0);
      });
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current?.duration || 0);
      });
      audioRef.current.addEventListener('ended', () => {
        playNext();
      });
    }
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentIndex !== null && tracks[currentIndex]) {
      const audio = audioRef.current;
      if (audio) {
        audio.src = tracks[currentIndex].audio_url;
        audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        setProgress(0);
      }
    }
  }, [currentIndex, tracks]);

  const playTrack = (index: number) => {
    const track = tracks[index];
    if (isYoutubeUrl(track.audio_url)) {
      setYoutubeTrack(track);
      return;
    }
    setCurrentIndex(index);
    if (!(track as MediaTrack & { demo?: boolean }).demo) {
      incrementTrackPlay(track.id).catch(() => {});
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || currentIndex === null) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const playNext = () => {
    const len = tracksRef.current.length;
    if (len === 0) return;
    setCurrentIndex((prev) => (prev === null ? 0 : (prev + 1) % len));
  };

  const playPrev = () => {
    const len = tracksRef.current.length;
    if (len === 0) return;
    setCurrentIndex((prev) => (prev === null ? 0 : (prev - 1 + len) % len));
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = value;
      setProgress(value);
    }
  };

  const handleLike = (track: MediaTrack) => {
    const isLiked = liked.has(track.id);
    const next = new Set(liked);
    if (isLiked) next.delete(track.id);
    else next.add(track.id);
    setLiked(next);
    if (!isLiked && !(track as MediaTrack & { demo?: boolean }).demo) {
      likeTrack(track.id).catch(() => {});
    }
  };

  if (tracks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
          <i className="ri-music-2-line text-3xl text-foreground-400" />
        </div>
        <p className="text-foreground-400">Aucune piste dans cette catégorie pour le moment.</p>
      </div>
    );
  }

  return (
    <div className="pb-28">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {tracks.map((track, index) => {
          const isCurrent = currentIndex === index;
          return (
            <button
              key={track.id}
              onClick={() => playTrack(index)}
              className={`group text-left rounded-xl overflow-hidden border transition-all duration-300 ${
                isCurrent
                  ? 'border-accent-400/60 bg-background-100'
                  : 'border-background-200/70 bg-background-50 hover:border-background-300/70'
              }`}
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={track.cover_url || ''}
                  alt={track.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                {/* Bouton play / égaliseur */}
                <div className="absolute bottom-3 right-3 w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950">
                  {isCurrent && isPlaying ? (
                    <i className="ri-equalizer-line text-lg animate-pulse" />
                  ) : (
                    <i className="ri-play-fill text-lg" />
                  )}
                </div>
                <span className="absolute bottom-3 left-3 inline-block px-2.5 py-1 rounded-full bg-background-950/50 backdrop-blur-sm text-xs text-foreground-50">
                  {formatTime(track.duration || 0)}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground-50 truncate">{track.title}</h3>
                <p
                  role="link"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (track.artist_name) {
                      navigate(`/media/artiste/${encodeURIComponent(track.artist_name)}`);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      if (track.artist_name) {
                        navigate(`/media/artiste/${encodeURIComponent(track.artist_name)}`);
                      }
                    }
                  }}
                  className="inline-block text-sm text-foreground-400 mt-0.5 hover:text-primary-400 transition-colors cursor-pointer"
                >
                  {track.artist_name || 'Artiste'}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-headphone-line" />
                      {formatCount(track.plays)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-map-pin-line text-accent-400" />
                      {track.location || 'Afrique'}
                    </span>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(track);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        handleLike(track);
                      }
                    }}
                    className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-accent-400 transition-colors"
                  >
                    <i
                      className={`${
                        liked.has(track.id) ? 'ri-heart-3-fill text-accent-400' : 'ri-heart-3-line'
                      }`}
                    />
                    {formatCount((track.likes || 0) + (liked.has(track.id) ? 1 : 0))}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Barre de lecture sticky */}
      {currentTrack && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background-950/95 backdrop-blur-md border-t border-background-300/60">
          <div className="w-full px-4 md:px-8 lg:px-12 py-3">
            <div className="flex items-center gap-4">
              {/* Cover + titre */}
              <img
                src={currentTrack.cover_url || ''}
                alt={currentTrack.title}
                className="w-12 h-12 rounded-lg object-cover object-top shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground-50 truncate">
                  {currentTrack.title}
                </p>
                <p
                  role="link"
                  tabIndex={0}
                  onClick={() => {
                    if (currentTrack.artist_name) {
                      navigate(`/media/artiste/${encodeURIComponent(currentTrack.artist_name)}`);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && currentTrack.artist_name) {
                      navigate(`/media/artiste/${encodeURIComponent(currentTrack.artist_name)}`);
                    }
                  }}
                  className="text-xs text-foreground-500 truncate hover:text-primary-400 transition-colors cursor-pointer"
                >
                  {currentTrack.artist_name || 'Artiste'}
                </p>
              </div>

              {/* Contrôles */}
              <div className="flex items-center gap-3">
                <button
                  onClick={playPrev}
                  className="w-9 h-9 flex items-center justify-center text-foreground-300 hover:text-foreground-50 transition-colors"
                  aria-label="Piste précédente"
                >
                  <i className="ri-skip-back-line text-xl" />
                </button>
                <button
                  onClick={togglePlay}
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 hover:bg-primary-400 transition-colors"
                  aria-label={isPlaying ? 'Pause' : 'Lecture'}
                >
                  <i className={`ri-${isPlaying ? 'pause' : 'play'}-fill text-xl`} />
                </button>
                <button
                  onClick={playNext}
                  className="w-9 h-9 flex items-center justify-center text-foreground-300 hover:text-foreground-50 transition-colors"
                  aria-label="Piste suivante"
                >
                  <i className="ri-skip-forward-line text-xl" />
                </button>
              </div>

              {/* Progression */}
              <div className="hidden sm:flex items-center gap-3 flex-1 max-w-xs">
                <span className="text-xs text-foreground-500 w-10 text-right">
                  {formatTime(progress)}
                </span>
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  value={progress}
                  onChange={handleSeek}
                  className="flex-1 accent-[oklch(var(--primary-500))]"
                  aria-label="Progression"
                />
                <span className="text-xs text-foreground-500 w-10">
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {youtubeTrack && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background-950/80 backdrop-blur-sm"
          onClick={() => setYoutubeTrack(null)}
        >
          <div
            className="relative w-full max-w-3xl rounded-xl overflow-hidden bg-background-950"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setYoutubeTrack(null)}
              className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-950/50 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-xl" />
            </button>
            <div className="aspect-video">
              <YoutubeEmbed
                url={youtubeTrack.audio_url}
                title={youtubeTrack.title}
                className="w-full h-full"
              />
            </div>
            <div className="p-4">
              <h3 className="font-heading font-semibold text-lg text-foreground-50">
                {youtubeTrack.title}
              </h3>
              <p
                role="link"
                tabIndex={0}
                onClick={() => {
                  if (youtubeTrack.artist_name) {
                    setYoutubeTrack(null);
                    navigate(`/media/artiste/${encodeURIComponent(youtubeTrack.artist_name)}`);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && youtubeTrack.artist_name) {
                    setYoutubeTrack(null);
                    navigate(`/media/artiste/${encodeURIComponent(youtubeTrack.artist_name)}`);
                  }
                }}
                className="inline-block text-sm text-foreground-500 mt-1 hover:text-primary-400 transition-colors cursor-pointer"
              >
                {youtubeTrack.artist_name || 'Artiste'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MusicPlayer;